import './App.css';
import { Routes, Route, useNavigate } from 'react-router-dom';
import { useEffect, useState, useCallback } from 'react';
import HowItWorks from './pages/HowItWorksPage';
import About from './pages/AboutPage';
import LoginPage from './pages/LoginPage';
import SignupPage from './pages/SignupPage';
import DashboardPage from './pages/DashboardPage';
import TasksPage from './pages/TaskPage';
import DashboardOverview from './pages/DashboardOverviewPage';
import BadgeRankingPage from './pages/LeaderboardPage';
import FeedbackPage from './pages/FeedbackPage';
import ProfilePage from './pages/ProfilePage';
import AdminPage from './pages/AdminPage';
import AdminLayout from './pages/AdminLayoutPage';
import PointSystemPage from './pages/PointSystemPage';
import ProtectedRoute from './ProtectedRoute';
import NotificationsPage from './pages/NotificationsPage';
import CompleteProfilePage from './pages/CompleteProfilePage';
import ResetPasswordRequest from './pages/ResetPasswordRequest';
import UpdatePassword from './pages/UpdatePassword';

import HomeContent from './components/Home/HomeContent';
import MainLayout from './components/Layout/MainLayout';
import { getCurrentSession, signOut, subscribeToAuthStateChanges } from './services/authService';
import { fetchProfileById } from './services/profileService';
import { isProfileComplete } from './utils/helpers';

function App() {
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [profileComplete, setProfileComplete] = useState(null);
  const [profile, setProfile] = useState(null);
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (!loading && session && profileComplete === false) {
      navigate('/complete-profile');
    }
  }, [loading, session, profileComplete, navigate]);

  const syncProfileState = useCallback(async (userId, logContext) => {
    const { data: profileData, error: profileError } = await fetchProfileById(userId);

    if (profileError) {
      console.error(`Error fetching profile${logContext}:`, profileError);
      setProfile(null);
      setProfileComplete(false);
      return;
    }

    if (profileData) {
      setProfile(profileData);
      setProfileComplete(isProfileComplete(profileData));
      return;
    }

    setProfile(null);
    setProfileComplete(false);
  }, []);

  useEffect(() => {
    const initializeAuth = async () => {
      const fetchedSession = await getCurrentSession();
      setSession(fetchedSession);

      if (fetchedSession?.user) {
        await syncProfileState(fetchedSession.user.id, '');
      } else {
        setProfile(null);
        setProfileComplete(true);
      }
      setLoading(false);
    };

    initializeAuth();

    const authSubscription = subscribeToAuthStateChanges(
      (_event, nextSession) => {
        setSession(nextSession);
        if (nextSession?.user) {
          syncProfileState(nextSession.user.id, ' on auth state change');
        } else {
          setProfileComplete(true);
          setProfile(null);
        }
      }
    );

    return () => {
      authSubscription.unsubscribe();
    };
  }, [syncProfileState]);

  const handleLogout = useCallback(async () => {
    console.log('Main handleLogout called'); // DEBUG
    const { error } = await signOut();
    if (error) console.error('Error signing out:', error.message);
    else navigate('/');
  }, [navigate]);

  const requestLogout = useCallback(() => {
    console.log('requestLogout called, showing modal'); // DEBUG
    setShowLogoutModal(true);
  }, []);

  // Modal Styles
  const overlayStyle = {
    position: "fixed",
    top: 0,
    left: 0,
    width: "100%",
    height: "100%",
    backgroundColor: "rgba(15, 23, 42, 0.6)", // Darker, more modern overlay
    backdropFilter: "blur(8px)", // Glassmorphism effect
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    zIndex: 9999,
    animation: "fadeIn 0.2s ease-out",
  };

  const modalStyle = {
    background: "rgba(255, 255, 255, 0.95)",
    padding: "30px",
    borderRadius: "24px", // More rounded
    width: "90%",
    maxWidth: "400px",
    textAlign: "center",
    boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)", // shadow-xl
    border: "1px solid rgba(255, 255, 255, 0.5)",
    transform: "scale(1)",
    animation: "scaleIn 0.2s ease-out",
  };

  const titleStyle = {
    fontSize: "1.5rem",
    fontWeight: "700",
    color: "#1e293b", // Slate-800
    marginBottom: "10px",
    marginTop: "0",
  };

  const messageStyle = {
    fontSize: "1rem",
    color: "#64748b", // Slate-500
    marginBottom: "25px",
    lineHeight: "1.5",
  };

  const buttonContainerStyle = {
    display: "flex",
    gap: "12px",
    justifyContent: "center",
  };

  const cancelButtonStyle = {
    padding: "12px 24px",
    borderRadius: "12px",
    border: "1px solid #e2e8f0", // Slate-200
    background: "white",
    color: "#475569", // Slate-600
    fontSize: "0.95rem",
    fontWeight: "600",
    cursor: "pointer",
    transition: "all 0.2s ease",
  };

  const logoutButtonStyle = {
    padding: "12px 24px",
    borderRadius: "12px",
    border: "none",
    background: "linear-gradient(135deg, #ef4444, #dc2626)", // Red gradient for destructive action
    color: "white",
    fontSize: "0.95rem",
    fontWeight: "600",
    cursor: "pointer",
    boxShadow: "0 4px 6px -1px rgba(220, 38, 38, 0.2)",
    transition: "all 0.2s ease",
  };

  return (
    <div className="App">
      <Routes>
        <Route path="/" element={<MainLayout session={session} onLogout={requestLogout} />}>
          <Route index element={<HomeContent />} />
          <Route path="how-it-works" element={<HowItWorks />} />
          <Route path="about" element={<About />} />

          <Route path="login" element={<LoginPage />} />
          <Route path="signup" element={<SignupPage />} />
          <Route path="dashboard" element={
            <ProtectedRoute user={session?.user} appLoading={loading} profile={profile} profileComplete={profileComplete}>
              <DashboardPage user={session?.user} />
            </ProtectedRoute>
          }>
            <Route index element={<DashboardOverview user={session?.user} />} />
            <Route path="tasks" element={<TasksPage user={session?.user} />} />
            <Route path="leaderboard" element={<BadgeRankingPage user={session?.user} />} />
            <Route path="notifications" element={<NotificationsPage user={session?.user} />} />
            <Route path="feedback" element={<FeedbackPage />} />
          </Route>
          <Route path="profile" element={
            <ProtectedRoute user={session?.user} appLoading={loading} profile={profile} profileComplete={profileComplete}>
              <ProfilePage user={session?.user} />
            </ProtectedRoute>
          } />
          <Route path="/complete-profile" element={<CompleteProfilePage user={session?.user} profile={profile} profileComplete={profileComplete} onProfileUpdate={() => syncProfileState(session?.user?.id)} />} />
          <Route path="reset-password" element={<ResetPasswordRequest />} />
          <Route path="update-password" element={<UpdatePassword />} />
        </Route>

        <Route
          path="/admin"
          element={
            <ProtectedRoute user={session?.user} appLoading={loading} profile={profile} profileComplete={profileComplete} requiredRole="admin">
              <AdminLayout />
            </ProtectedRoute>
          }
        >
          <Route index element={<AdminPage user={session?.user} />} />
          <Route path="points" element={<PointSystemPage user={session?.user} />} />
          <Route path="leaderboard" element={<BadgeRankingPage user={session?.user} />} />
          <Route path="profile" element={<ProfilePage user={session?.user} />} />
        </Route>
      </Routes>
      {showLogoutModal && (
        <div style={overlayStyle}>
          <div style={modalStyle}>
            <h3 style={titleStyle}>Sign Out?</h3>
            <p style={messageStyle}>Are you sure you want to end your session?</p>

            <div style={buttonContainerStyle}>
              <button
                style={cancelButtonStyle}
                onClick={() => setShowLogoutModal(false)}
                onMouseEnter={(e) => e.target.style.background = "#f1f5f9"}
                onMouseLeave={(e) => e.target.style.background = "white"}
              >
                Cancel
              </button>

              <button
                style={logoutButtonStyle}
                onClick={async () => {
                  setShowLogoutModal(false);
                  await handleLogout();
                }}
                onMouseEnter={(e) => e.target.style.transform = "translateY(-1px)"}
                onMouseLeave={(e) => e.target.style.transform = "translateY(0)"}
              >
                Yes, Sign Out
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
export default App;
